import React from 'react'

import { Box } from '@mui/material'

import { Skeleton } from '@repo/ui'

/**
 * Rendered height of ONE line of body prose, per typography variant, in px.
 *
 * These are the numbers a paragraph actually occupies, and they are what a skeleton standing in
 * for that paragraph has to reserve — not the height of the shimmer bar it draws.
 *
 * Derived, not guessed: the shared scale (`packages/ui/theme/typography.ts`) gives `body3` a
 * 14px font and `body4` a 12px font, and every prose block in the support surfaces overrides the
 * scale's flat `18px` with `lineHeight: 1.5` so descriptive copy does not read dense (§15). So a
 * line box is 14 × 1.5 = 21 and 12 × 1.5 = 18. `body2` titles use `lineHeight: 1.4` → 16 × 1.4 =
 * 22.4. All three confirmed against `getBoundingClientRect` in the browser.
 */
export const PROSE_LINE_H = {
  /** `body3` at `lineHeight: 1.5` — request bodies, announcement bodies, card excerpts. */
  body3: 21,
  /** `body4` at `lineHeight: 1.5` — captured context, board descriptions, "Raised by". */
  body4: 18,
  /** `body2` at `lineHeight: 1.4` — the announcement card's title. */
  body2Title: 22.4,
} as const

interface ProseSkeletonProps {
  /** How many rendered lines of prose this stands in for. */
  lines: number
  /** Line box of the text being stood in for. Defaults to `body3`. */
  lineHeight?: number
  /** Bar thickness. 14 reads as a line of text without filling the whole line box. */
  barHeight?: number
  /**
   * Gap BETWEEN the lines, for a block whose lines are separate elements in a `Stack` rather
   * than a wrapped paragraph.
   *
   * A wrapped paragraph has no gap — its line boxes abut — so this defaults to 0. But the
   * request page's captured-context block is a `<Stack spacing={1}>` of one `Typography` per
   * fact, which puts 4px between them. Ignoring that undercounted the block by exactly one gap
   * and left the reply box 4px high.
   */
  gap?: number
}

/**
 * A skeleton for a block of prose that occupies EXACTLY the space that prose will (§19).
 *
 * ## Why this exists rather than `<Skeleton lines={n} />`
 *
 * The shared primitive stacks `n` bars of `height` with a 4px gap, so `lines={3}` occupies
 * 3 × 14 + 2 × 4 = 50px. The three lines of `body3` it stands in for occupy 3 × 21 = 63px. That
 * 13px shortfall is per block and it compounds: measured on `/whats-new`, the feed skeleton's
 * two-line body stood in for a three-line one and every card below it landed 18.6px off.
 *
 * So the WRAPPER carries the real footprint and the bars are positioned inside it. The bars stay
 * 14px — a shimmer filling the whole 21px line box would read as a solid block rather than text
 * — and `space-between` plus half a leading of padding lands each bar exactly on the line box its
 * text will occupy, so the swap is a fade rather than a slide.
 *
 * A ONE-line block gets the short bar, not a full-width one: a single line of a paragraph is all
 * ragged end, and `Skeleton`'s own `lines` treatment (last bar at 60%) says the same thing.
 *
 * Belongs in `@repo/ui` beside `Skeleton` itself; it is here because that file is being edited
 * elsewhere. Move it when that lands — nothing about it is support-specific.
 */
export const ProseSkeleton = ({
  lines,
  lineHeight = PROSE_LINE_H.body3,
  barHeight = 14,
  gap = 0,
}: ProseSkeletonProps) => {
  if (lines <= 1) {
    return (
      <Box sx={{ height: lineHeight, display: 'flex', alignItems: 'center' }}>
        <Skeleton width="60%" height={barHeight} />
      </Box>
    )
  }

  return (
    <Skeleton
      lines={lines}
      height={barHeight}
      sx={{
        height: lines * lineHeight + (lines - 1) * gap,
        justifyContent: 'space-between',
        // Half the leading, so the first and last bars sit inside their own line boxes rather
        // than flush against the block's edges.
        py: `${(lineHeight - barHeight) / 2}px`,
      }}
    />
  )
}
